/**
 * 图片另存为（任务 12c）。
 *
 * 一律先 fetch→Blob（远端图需 CDN 放行 CORS 的 GET；blob: URL 同路径统一处理）：
 * - 跨域 `<a download>` 会被浏览器忽略 download 属性直接导航，必须经 Blob；
 * - 文件名扩展按 **Blob 的 MIME** 定——远端对象统一是 `.acg` 防直链扩展名
 *   （protocol §10.1），不能直接当文件扩展名保存。
 *
 * 路径选择由调用方传 preferShare（移动布局或 standalone）：
 * - share：navigator.share({files}) 调起系统面板（iOS standalone 下载体验差，
 *   PROMPT 硬口径「保存优先 share」）；用户取消（AbortError）不算失败静默返回。
 * - download：Blob→objectURL→`<a download>`，桌面标准下载。
 * share 不可用（API 缺失/canShare 拒绝文件）自动落回 download。
 */

const MIME_EXT: Readonly<Record<string, string>> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
};

/** 按 URL 路径名 + MIME 合成保存文件名（纯函数，可单测） */
export function imageFileName(url: string, mime: string): string {
  let base = '';
  try {
    // blob:/相对路径都解析不出有意义的 pathname 时走回退名
    const path = new URL(url, 'https://x.invalid').pathname;
    const last = path.slice(path.lastIndexOf('/') + 1);
    base = decodeURIComponent(last);
  } catch {
    base = '';
  }
  const dot = base.lastIndexOf('.');
  const stem = (dot > 0 ? base.slice(0, dot) : base) || `image-${Date.now()}`;
  const urlExt = dot > 0 ? base.slice(dot + 1).toLowerCase() : '';
  const ext =
    MIME_EXT[mime] ?? (urlExt !== '' && urlExt !== 'acg' && urlExt.length <= 5 ? urlExt : 'jpg');
  return `${stem}.${ext}`;
}

export type SaveImageResult = 'ok' | 'failed';

/**
 * suggestedName（14.3 文件/视频）：envelope 自带原始文件名（含真实扩展名）时
 * 直接沿用，不走 URL+MIME 推导（远端对象是 .acg、通用文件 MIME 推不出扩展名）。
 */
export async function saveImage(
  url: string,
  preferShare: boolean,
  suggestedName?: string,
): Promise<SaveImageResult> {
  let blob: Blob;
  try {
    const res = await fetch(url);
    if (!res.ok) return 'failed';
    blob = await res.blob();
  } catch {
    return 'failed'; // 远端 CORS 拒绝 / 网络错
  }
  const named = suggestedName !== undefined && suggestedName !== '';
  const name = named ? suggestedName : imageFileName(url, blob.type);
  return saveBlob(blob, name, preferShare, named ? 'application/octet-stream' : 'image/jpeg');
}

/**
 * 已持有 Blob 的保存入口（14.32 二维码导出首用；saveImage 的 fetch 之后同一条
 * 路径）。shareTypeFallback 只在 blob.type 为空时兜 File 类型（canShare 判定用）。
 */
export async function saveBlob(
  blob: Blob,
  name: string,
  preferShare: boolean,
  shareTypeFallback = 'application/octet-stream',
): Promise<SaveImageResult> {
  if (preferShare && typeof navigator.share === 'function') {
    const file = new File([blob], name, {
      type: blob.type || shareTypeFallback,
    });
    if (navigator.canShare?.({ files: [file] }) === true) {
      try {
        await navigator.share({ files: [file] });
        return 'ok';
      } catch (err) {
        // 用户取消分享面板不是失败；其余（权限/环境）落回下载路径
        if (err instanceof DOMException && err.name === 'AbortError') return 'ok';
      }
    }
  }

  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // 回收延后：部分浏览器 click 后异步读取 objectURL，立即 revoke 会截断下载
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
  return 'ok';
}
